import { useState, useEffect, useCallback } from 'react'
import { supabase, safeQuery } from '../lib/supabase'
import { getCached, setCached } from '../lib/queryCache'
import { useAutoRefresh } from './useAutoRefresh'
import { localFallback, localFirstRows, localUpsert, localDelete, raceTimeout } from '../lib/syncEngine'
import { invalidate } from '../lib/dataInvalidation'
import { queuedWrite } from '../lib/queuedWrite'

const TASK_SELECT = '*'

// ─── List tasks ─────────────────────────────────────────────────────────────

// A task is "suggested" (not the user's own to-do) when it's awaiting human
// review (status 'suggested' — every automated creator now inserts with it),
// when Janet auto-created it from an email and nobody has confirmed it, OR it
// was neither entered by nor assigned to the current user — those don't belong
// in someone's personal list, they're suggestions to triage.
function taskIsSuggested(t, userId) {
  if (t.status === 'suggested') return true
  if (t.source_email_id && !t.created_by) return true
  if (!userId) return false
  const assignees = Array.isArray(t.assigned_to) ? t.assigned_to : (t.assigned_to ? [t.assigned_to] : [])
  return !assignees.includes(userId) && t.created_by !== userId
}

export function useTasks(filters = {}) {
  const { company_id, status, quadrant, search, assigned_to, suggested, current_user_id } = filters
  const cacheKey = `tasks:${company_id||''}:${status||''}:${quadrant||''}:${search||''}:${assigned_to||''}:${suggested||''}:${current_user_id||''}`
  const cached = getCached(cacheKey)
  const [tasks, setTasks] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)

  const fetchTasks = useCallback(async () => {
    console.log('Fetching tasks...')
    if (!getCached(cacheKey)) setLoading(true)
    setError(null)
    // Local-first paint (desktop shell only): instant rows from the on-disk
    // mirror while the network fetch runs; a network SUCCESS replaces them
    // wholesale (networkOk guard — a fast network failure must not suppress
    // the local paint). Supported filters (status 'open' + suggested) are
    // applied client-side to the local rows exactly like the network
    // post-processing below; any other filter combination skips the paint.
    let networkOk = false
    const canPaintLocal = !company_id && !quadrant && !search && !assigned_to
      && (!status || status === 'open') && !getCached(cacheKey)
    if (canPaintLocal) {
      localFirstRows('tasks', { orderBy: 'due_date ASC NULLS LAST' }).then((local) => {
        if (!local || networkOk || getCached(cacheKey)?.length) return
        let rows = local
        if (status === 'open') {
          const open = suggested === 'only'
            ? ['pending', 'in_progress', 'suggested']
            : ['pending', 'in_progress']
          rows = rows.filter(t => open.includes(t.status))
        }
        if (suggested === 'only') {
          rows = rows
            .filter(t => taskIsSuggested(t, current_user_id))
            .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        } else if (suggested === 'exclude') {
          rows = rows.filter(t => !taskIsSuggested(t, current_user_id))
        }
        if (!rows.length || networkOk) return
        setTasks(rows); setTotal(rows.length); setLoading(false)
      }).catch(() => {})
    }
    try {
      let query = supabase
        .from('tasks')
        .select(TASK_SELECT, { count: 'estimated' })
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('priority', { ascending: true })

      if (company_id) query = query.eq('company_id', company_id)
      // 'open' is a virtual status covering anything still actionable. The
      // Suggested tab ('only') also needs status='suggested' rows — those are
      // auto-created tasks awaiting review, excluded from every other view.
      if (status === 'open') {
        query = query.in('status', suggested === 'only'
          ? ['pending', 'in_progress', 'suggested']
          : ['pending', 'in_progress'])
      } else if (status) {
        query = query.eq('status', status)
      } else if (suggested === 'only') {
        query = query.in('status', ['pending', 'in_progress', 'suggested'])
      }
      if (quadrant) query = query.eq('quadrant', parseInt(quadrant))
      // assigned_to is uuid[] — contains (cs) only. The old `.or(…assigned_to.eq.<uuid>)`
      // compared an array to a single uuid, which Postgres rejects (400), so the
      // assignee filter failed every time it was used (found 2026-10-15).
      if (assigned_to) query = query.filter('assigned_to', 'cs', `{${assigned_to}}`)

      const { data, error: err, count } = await safeQuery(query)
      if (err) {
        setError(err.message)
        if (!getCached(cacheKey)) {
          const local = await localFallback('tasks', { orderBy: 'due_date ASC NULLS LAST' })
          if (local.length) { setTasks(local); setTotal(local.length) }
        }
        return
      }

      networkOk = true // fresh rows win; suppress any late local-first paint
      let results = data || []
      if (search) {
        const s = search.toLowerCase()
        results = results.filter(t =>
          t.title?.toLowerCase().includes(s) ||
          t.description?.toLowerCase().includes(s)
        )
      }

      // 'only' powers the Suggested Tasks tab (newest first); 'exclude' keeps
      // suggested tasks out of the main views so they don't clutter real to-dos.
      if (suggested === 'only') {
        results = results
          .filter(t => taskIsSuggested(t, current_user_id))
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      } else if (suggested === 'exclude') {
        results = results.filter(t => !taskIsSuggested(t, current_user_id))
      }

      setTasks(results)
      setTotal((search || suggested) ? results.length : (count || 0))
      setCached(cacheKey, results)
    } catch (err) {
      setError(err.message)
      if (!getCached(cacheKey)) {
        const local = await localFallback('tasks', { orderBy: 'due_date ASC NULLS LAST' })
        if (local.length) { setTasks(local); setTotal(local.length) }
      }
    } finally {
      setLoading(false)
    }
  }, [company_id, status, quadrant, search, assigned_to, suggested, current_user_id, cacheKey])

  useEffect(() => { fetchTasks() }, [fetchTasks])

  const silentRefetch = useCallback(() => fetchTasks(), [fetchTasks])
  useAutoRefresh(silentRefetch)

  return { tasks, loading, error, total, refetch: fetchTasks }
}

// ─── Single task ────────────────────────────────────────────────────────────

export function useTask(id) {
  const [task, setTask] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTask = useCallback(async () => {
    if (!id) return
    console.log('Fetching task...')
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await safeQuery(
        supabase
          .from('tasks')
          .select(TASK_SELECT)
          .eq('id', id)
          .single()
      )
      if (err) { setError(err.message); return }
      setTask(data)
    } catch (err) {
      setError(err.message)
      setTask(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { fetchTask() }, [fetchTask])

  return { task, loading, error, refetch: fetchTask }
}

// ─── Sanitize payload before sending to Supabase ────────────────────────────
// assigned_to may be stored as Postgres uuid OR uuid[] depending on when the
// column was created. The form uses MultiUserPicker which yields
// [{id, full_name, email, label}] objects; this function flattens to an
// array of uuid strings and, if the DB column is still singular uuid,
// falls back to the first uuid so inserts don't 400.
//
// JSONB linked_* columns are fine as empty arrays but we coerce nulls to [].
//
// Only fields present in the input are normalized — a field the caller didn't
// pass must stay absent, so partial updates (e.g. {status, completed_at}) don't
// null out assigned_to or wipe the linked_* arrays on the row.
const LINKED_COLS = ['linked_contacts','linked_projects','linked_properties','linked_opportunities','linked_deals']

function sanitizeTaskPayload(data) {
  const p = { ...data }

  if ('assigned_to' in p) {
    if (Array.isArray(p.assigned_to)) {
      const ids = p.assigned_to
        .map(x => (typeof x === 'string' ? x : (x && x.id) || null))
        .filter(Boolean)
      p.assigned_to = ids.length === 0 ? null : ids
    } else if (p.assigned_to && typeof p.assigned_to === 'object') {
      // Single object from a picker that only allows one selection
      p.assigned_to = p.assigned_to.id || null
    } else if (!p.assigned_to) {
      p.assigned_to = null
    }
  }

  // priority: DB column is INTEGER (1=top, 2=active/medium, 3=general/low).
  // Coerce common string labels so older callers passing 'high'/'medium'/'low'
  // don't blow up with "invalid input syntax for type integer".
  if (typeof p.priority === 'string') {
    const map = { high: 1, top: 1, urgent: 1, medium: 2, mid: 2, normal: 2, active: 2, low: 3, general: 3 }
    const mapped = map[p.priority.toLowerCase().trim()]
    if (mapped) p.priority = mapped
    else if (/^\d+$/.test(p.priority)) p.priority = parseInt(p.priority, 10)
    else delete p.priority   // unrecognized — let DB default
  }

  // JSONB linked arrays — null → [], but only when the caller passed the field
  for (const col of LINKED_COLS) {
    if (col in p && !p[col]) p[col] = []
  }
  return p
}

// Retry helper: if Postgres complains that assigned_to is uuid (not uuid[]),
// re-send with the first id as a scalar string.
function flattenAssignedToOnError(data, err) {
  if (!err || !err.message) return null
  const msg = String(err.message)
  if (!/invalid input syntax for type uuid/i.test(msg)) return null
  if (!Array.isArray(data.assigned_to)) return null
  const first = data.assigned_to[0] || null
  return { ...data, assigned_to: first }
}

// ─── CRUD ───────────────────────────────────────────────────────────────────

export async function createTask(data) {
  const payload = sanitizeTaskPayload(data)
  // Inserts still default omitted fields — new rows should never hold null linked_* arrays
  if (!('assigned_to' in payload)) payload.assigned_to = null
  for (const col of LINKED_COLS) {
    if (!(col in payload)) payload[col] = []
  }
  let result
  try {
    result = await queuedWrite('tasks', 'insert', payload)
  } catch (error) {
    // If the DB column is still singular uuid, retry with scalar
    const fallback = flattenAssignedToOnError(payload, error)
    if (fallback) {
      result = await queuedWrite('tasks', 'insert', fallback)
    } else {
      throw error
    }
  }
  localUpsert('tasks', result).catch(() => {})
  invalidate('tasks', { id: result?.id, op: 'create' })

  // Notify assignees (fire-and-forget)
  if (result?.id && result?.assigned_to) {
    fetch('/api/task-assign-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: result.id }),
    }).catch(() => {})
  }

  return result
}

// ─── Suggested-task review actions ──────────────────────────────────────────
// Approve/dismiss bypass sanitizeTaskPayload on purpose: these are targeted
// status flips and must not touch assigned_to / linked_* fields.

export async function approveSuggestedTask(task, userId) {
  const patch = { status: 'pending', updated_at: new Date().toISOString() }
  // Stamp the approver as creator so the task counts as human-owned from here on
  if (!task.created_by && userId) patch.created_by = userId
  const result = await queuedWrite('tasks', 'update', patch, task.id)
  localUpsert('tasks', result).catch(() => {})
  invalidate('tasks', { id: task.id, op: 'update' })
  return result
}

export async function dismissSuggestedTask(taskId) {
  const patch = { status: 'cancelled', updated_at: new Date().toISOString() }
  const result = await queuedWrite('tasks', 'update', patch, taskId)
  localUpsert('tasks', result).catch(() => {})
  invalidate('tasks', { id: taskId, op: 'update' })
  return result
}

export async function updateTask(id, data) {
  const wasCompleted = data.status === 'completed'
  const payload = { ...sanitizeTaskPayload(data), updated_at: new Date().toISOString() }
  if (wasCompleted && !data.completed_at) {
    payload.completed_at = new Date().toISOString()
  }

  let result
  try {
    result = await queuedWrite('tasks', 'update', payload, id)
  } catch (error) {
    // If DB column is still singular uuid (not uuid[]), retry with scalar
    const fallback = flattenAssignedToOnError(payload, error)
    if (fallback) {
      result = await queuedWrite('tasks', 'update', fallback, id)
    } else {
      const msg = error?.message || error?.details || JSON.stringify(error) || 'Unknown error'
      throw new Error(msg)
    }
  }

  // Fire-and-forget completion notification to the task creator
  if (wasCompleted && result?.created_by) {
    fetch('/api/task-complete-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: id }),
    }).catch(() => {})
  }

  // Notify assignees when assigned_to was explicitly set in this update
  if (!wasCompleted && 'assigned_to' in data && data.assigned_to) {
    fetch('/api/task-assign-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: id }),
    }).catch(() => {})
  }

  localUpsert('tasks', result).catch(() => {})
  invalidate('tasks', { id, op: 'update' })
  return result
}

export async function deleteTask(id) {
  await queuedWrite('tasks', 'delete', null, id)
  localDelete('tasks', id).catch(() => {})
  invalidate('tasks', { id, op: 'delete' })
}

// ─── Typeahead search (used by Ask Janet bar) ─────────────────────────────────
// Filters stop words / <3 char words so question-shaped queries don't flood.
const TSK_STOP_WORDS = new Set([
  'the','a','an','of','to','in','on','at','by','for','with','and','or','but',
  'is','are','was','were','be','been','being','have','has','had',
  'do','does','did','doing','i','you','he','she','it','we','they','me','him','her',
  'this','that','these','those','when','what','who','where','why','how','which','soon',
])

export async function searchTasks(query, { limit = 10 } = {}) {
  if (!query || query.length < 1) return []
  const words = query.trim().toLowerCase().split(/\s+/)
    .filter(w => w.length >= 3 && !TSK_STOP_WORDS.has(w))
    .slice(0, 4)
  if (words.length === 0) return []

  const orClauses = words
    .map(w => `title.ilike.%${w}%,description.ilike.%${w}%`)
    .join(',')

  try {
    const { data, error } = await raceTimeout(supabase
      .from('tasks')
      .select('id, title, quadrant, status, due_date')
      .or(orClauses)
      .neq('status', 'completed')
      .neq('status', 'cancelled')
      .order('due_date', { ascending: true, nullsFirst: false })
      .limit(limit), 7000, 'searchTasks')
    if (error) return []
    return data || []
  } catch { return [] }
}
